<script setup lang="ts">
import { computed } from 'vue';

const appConfig = useAppConfig();
const shellConfig = computed(() => appConfig.shellConfig ?? {});
</script>

<template>
  <div class="min-h-screen flex flex-col bg-white text-gray-900">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-900 focus:px-4 focus:py-3 focus:text-white"
    >
      Skip to main content
    </a>
    <header class="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
      <nav
        class="flex items-center justify-between px-4 sm:px-6 py-4 max-w-7xl mx-auto"
        aria-label="Primary navigation"
      >
        <NuxtLink
          to="/"
          class="flex items-center transition-opacity hover:opacity-80"
        >
          <img
            :src="shellConfig.logo?.src"
            :alt="shellConfig.logo?.alt ?? 'QikSolve'"
            width="411"
            height="141"
            class="h-8 w-auto"
          >
        </NuxtLink>
        <ul class="hidden lg:flex items-center gap-5 text-sm font-medium">
          <li
            v-for="item in shellConfig.navigationMenu"
            :key="item.route"
          >
            <NuxtLink
              :to="item.route"
              class="text-gray-600 hover:text-brand-600 transition-colors"
              active-class="text-brand-700"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
        </ul>
        <div class="hidden sm:flex items-center gap-3">
          <NuxtLink
            to="/contact"
            class="inline-flex min-h-10 items-center whitespace-nowrap rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            Get In Touch
          </NuxtLink>
        </div>
        <details class="relative lg:hidden">
          <summary class="flex size-10 cursor-pointer list-none items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600">
            <span class="sr-only">Open navigation</span>
            <span
              class="flex w-5 flex-col gap-1"
              aria-hidden="true"
            >
              <span class="h-0.5 w-full rounded bg-current" />
              <span class="h-0.5 w-full rounded bg-current" />
              <span class="h-0.5 w-full rounded bg-current" />
            </span>
          </summary>
          <div class="absolute right-0 top-12 z-50 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
            <ul class="space-y-1 text-sm font-medium">
              <li
                v-for="item in shellConfig.navigationMenu"
                :key="`mobile-${item.route}`"
              >
                <NuxtLink
                  :to="item.route"
                  class="block rounded-lg px-3 py-2.5 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  active-class="bg-brand-50 text-brand-700"
                >
                  {{ item.label }}
                </NuxtLink>
              </li>
            </ul>
            <div class="mt-3 grid gap-2 border-t border-gray-100 pt-3">
              <NuxtLink
                to="/contact"
                class="flex min-h-10 items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                Get In Touch
              </NuxtLink>
            </div>
          </div>
        </details>
      </nav>
    </header>

    <main
      id="main-content"
      class="flex-1"
    >
      <slot />
    </main>

    <footer class="border-t border-gray-200 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p class="flex items-center gap-2 text-sm text-gray-500">
          <img
            :src="shellConfig.logo?.iconSrc"
            :alt="shellConfig.logo?.alt ?? 'QikSolve'"
            width="32"
            height="32"
            loading="lazy"
            decoding="async"
            class="size-4 rounded-sm"
          >
          &copy; {{ shellConfig.footer?.copyrightYear }} {{ shellConfig.footer?.companyName ?? 'QikSolve' }}
        </p>
        <ul class="flex gap-6 text-sm">
          <li
            v-for="link in shellConfig.footer?.links"
            :key="link.route"
          >
            <NuxtLink
              :to="link.route"
              class="text-gray-500 hover:text-brand-600 transition-colors"
            >
              {{ link.label }}
            </NuxtLink>
          </li>
        </ul>
      </div>
    </footer>
  </div>
</template>

